import { ReactNode, forwardRef } from "react";
import { Box, BoxProps, Fade, Stack, useTheme } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Button } from "src/components-mui/base/button/button";
import { FADE_TRANSITION_PROPS } from "src/components-mui/base/fade/fade-transition.constants";
import { useUserStore } from "src/stores/user-store";
import { NAV_SEARCH_TEST_ID } from "./global-nav-search-ui.test-ids";

interface IProps extends Omit<BoxProps, "ref"> {
    isOpen?: boolean;
    navHeights: number[];
    onSearchClose: () => void;
    children: ReactNode;
}

export const GlobalNavSearchUi = forwardRef<HTMLDivElement, IProps>(function GlobalNavSearchUi(
    { navHeights, onSearchClose, isOpen = true, children, sx, ...otherProps },
    ref
) {
    const { t } = useTranslation("common");
    const { isLoggedIn } = useUserStore();
    const theme = useTheme();
    const leftAdjustment = isLoggedIn ? [0, 0, "122px", "112px"] : [0, 0, "214px", "204px"];
    const getNavHeight = (spacingFactor: number | undefined) => (spacingFactor === undefined ? undefined : theme.spacing(spacingFactor));

    return (
        <Fade in={isOpen} mountOnEnter unmountOnExit {...FADE_TRANSITION_PROPS}>
            <Box
                data-test-id={NAV_SEARCH_TEST_ID}
                sx={[
                    {
                        position: "fixed",
                        top: 0,
                        right: 0,
                        left: leftAdjustment,
                        height: ["100vh", "100vh", getNavHeight(navHeights[2]), getNavHeight(navHeights[3])],
                        overflowY: ["auto", "auto", "visible", "visible"],
                        backgroundColor: "white",
                        zIndex: "modal",
                        p: [3, 5, 1],
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
                {...otherProps}
            >
                <Stack
                    ref={ref}
                    direction={["column", "column", "row-reverse"]}
                    sx={{
                        height: ["auto", "auto", "100%", "100%"],
                        alignItems: ["flex-end", "flex-end", "flex-start"],
                        justifyContent: ["initial", "initial", "space-between"],
                        gap: [2, 2, 8],
                    }}
                >
                    <Button
                        variant="ghost"
                        size="small"
                        onClick={onSearchClose}
                        sx={{
                            alignSelf: ["flex-end", "flex-end", "center", "center"],
                            mr: [-2, -2, 3],
                            mt: [-2, -2, 0],
                        }}
                    >
                        {t("close")}
                    </Button>
                    <Box sx={{ pt: 1, width: ["100%", "100%", "720px", "820px"] }}>{children}</Box>
                </Stack>
            </Box>
        </Fade>
    );
});
